/**
 * EDITOR NOTES — the review record, and the control that unblocks a resubmit.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ THE PROTOTYPE HAS NO RESOLVE CONTROL AT ALL, AND WITHOUT ONE `resubmitArticle` IS REFUSED
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `docs/ui/04-pages.md:467` and `:657`: `resolveReviewFinding` is one of two operations the
 * prototype gives no control for *"and that gate other things"* — resubmit answers
 * `BLOCKING_ISSUES_UNRESOLVED` while any blocking finding is open. The 200 of that operation
 * carries `openBlockingCount`, *"the count the Resubmit button is gated on"*
 * (authors-api.d.ts:4801-4803), so pressing "Mark as resolved" moves the gate on the
 * response and the pane does not re-read the whole review to find out.
 *
 * ★★ THE REAL RECORD IS FAR RICHER THAN A NOTICE BOARD, and every member of it is rendered here:
 * `severity` (four bands), `isBlocking` — **independent of severity**, so a low finding may block
 * and a high one may not — `label`, `authorGuidance`, `disposition`, the optional
 * `suggestedRewrite`, and the anchor. The prototype draws a flat list of notes.
 *
 * ★★ AND THE ANCHOR IS RE-RESOLVED HERE RATHER THAN TRUSTED. `anchorState` is how the span
 * resolved **at the verdict**; the author has been editing since. So each span finding is run back
 * through `relocate` — the same ordered strategy the moderation worker runs, from the same file
 * (`shared/content-model/anchors.ts:467`, and `test/shared/content-model/anchor-agreement.spec.ts`
 * is what holds the two callers together). Three answers and three renderings: still in place,
 * moved (and shown where it moved to), or gone — and **a finding whose anchor is gone keeps its
 * quote**, so the author still sees what was meant.
 *
 * ★ OFFSETS ARE UNICODE CODE POINTS, INCLUSIVE START AND EXCLUSIVE END. Every slice below goes
 * through `sliceCp`; `String.prototype.slice` counts UTF-16 units and would cut an emoji in half.
 * The unit is named in the contract on purpose: *"the ruled risk is a UTF-16 index stored in a
 * field called an offset"*.
 *
 * ★ "REPLY IF YOU DISAGREE" IS NOT DRAWN. `resolveReviewFinding` carries **no message body** —
 * verified on the operation, which declares `requestBody?: never` — so the pane's promise of a
 * conversation has nothing behind it (`docs/ui/04-pages.md:468`, PLATFORM WORK OWED). Filed in
 * OPEN rather than mocked up.
 *
 * ★ NO MODERATION NUMBER APPEARS. No score, no band, no threshold, no per-finding deduction —
 * *"No number from the moderation pipeline appears here"* (authors-api.yaml:4907-4909). `stage` and
 * `taxonomyCode` are codes, not measurements - and since design 15 no code is shown (plain words).
 */
import { Badge, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { blockText, relocateFinding, sliceCp } from '../../../features/cabinet/api/editor-measure';
import type { Block, ReviewFinding, ReviewRecord } from '../../../features/cabinet/api/editor-api';
import { useTranslation } from '../../../i18n';
import { usePieceContext } from './piece-context';

export interface ReviewNotesProps {
  readonly review: ReviewRecord;
  /** The author's CURRENT document. The anchors are resolved against this, not against the version. */
  readonly blocks: readonly Block[];
  readonly onResolve: (finding: ReviewFinding) => void;
  readonly resolvingOrdinal: number | undefined;
  readonly disabled: boolean;
}

const SEVERITY_TOKEN: Readonly<Record<ReviewFinding['severity'], string>> = {
  // `m3`, the rung the status pill's `muted` tone takes. This named `--mx-muted`, which nothing
  // emits, so "Minor" drew no tint at all and its words inherited the card's ink. On its own tint
  // `m3` reads 5.78 light and 4.82 dark (web/authors/test/tinted-inks.spec.tsx).
  low: '--mx-m3',
  medium: '--mx-warning',
  high: '--mx-warning',
  critical: '--mx-danger',
};

/**
 * THE BADGE'S WORDS, which are not always its tint. Warning TEXT takes the warning INK, web/ui's
 * per-scheme alias - light `accentHover`, dark `warning` - because light `warning` on its own tint
 * read 4.09 in an open note; the ink reads 6.21, dark 8.54 either way
 * (web/authors/test/tinted-inks.spec.tsx).
 */
const SEVERITY_INK: Readonly<Record<ReviewFinding['severity'], string>> = {
  ...SEVERITY_TOKEN,
  medium: '--mx-warning-ink',
  high: '--mx-warning-ink',
};

/**
 * A badge's tint, laid on an opaque base of its own - the card colour, as the status pill's is
 * (../../../features/cabinet/components/status-pill.tsx) - so a closed note's quieter ground below
 * never reaches the words: bare, light `danger` would read 4.45 on it rather than 4.82.
 */
function tintedBadge(tint: string, ink: string): CSSProperties {
  const wash = `oklch(var(${tint}) / 0.14)`;
  return {
    backgroundColor: 'var(--mantine-color-body)',
    backgroundImage: `linear-gradient(${wash}, ${wash})`,
    color: `oklch(var(${ink}))`,
  };
}

/*
 * A closed note's state is said in the words this screen has always used for it: the catalogue's
 * `editor.reviewNotes.closed` on its badge, `closedInName` in its accessible name.
 */

/**
 * ★ A CLOSED NOTE IS MARKED, NOT FADED (wave 14; the screen's layout is ours, decision-log rows
 * 99/100/108). It used to fade its whole card to `opacity: 0.78`, and a multiplier lands on every
 * word in the card: its badges fell to light `danger` 3.46 and the warning ink 3.80, dark `danger`
 * 3.63, and no ink could pay for it. Now the state is SAID - the badge beside the note and the
 * note's accessible name carry the screen's own words for it - and SHOWN by a quieter card that
 * dims nothing: the page ground under a dashed border in `border-dash` (a border, not text). Every
 * badge reads as it does on an open note, and `dimmed` reads 6.52 light, 7.74 dark on this ground
 * (web/authors/test/tinted-inks.spec.tsx).
 */
const CLOSED_CARD: CSSProperties = {
  backgroundColor: 'oklch(var(--mx-bg))',
  borderStyle: 'dashed',
  borderColor: 'oklch(var(--mx-border-dash))',
};

/* The severity's words are the catalogue's `editor.reviewNotes.severity`. */

/** Where this finding points in the document as it stands now. */
type Placement =
  | { kind: 'article' }
  | { kind: 'block'; ordinal: number }
  | { kind: 'span'; ordinal: number; before: string; quote: string; after: string; moved: boolean }
  | { kind: 'lost'; quote: string };

function placementOf(finding: ReviewFinding, blocks: readonly Block[]): Placement {
  if (finding.anchorScope === 'article') {
    return { kind: 'article' };
  }
  const index = blocks.findIndex((block) => block.blockId === finding.blockId);
  if (finding.anchorScope === 'block') {
    return index < 0 ? { kind: 'article' } : { kind: 'block', ordinal: index + 1 };
  }

  const block = index < 0 ? undefined : blocks[index];
  const text = block === undefined ? null : blockText(block);
  if (text === null) {
    /* THE BLOCK IS GONE OR CARRIES NO TEXT. The quote survives the loss by design, so it is still
       shown — "a finding whose anchor could not be re-found degrades to `unanchored` and KEEPS ITS
       QUOTE" (authors-api.yaml's ReviewFinding). */
    return { kind: 'lost', quote: finding.quoteExact ?? '' };
  }

  const where = relocateFinding(finding, text);
  if (where === null) {
    return { kind: 'lost', quote: finding.quoteExact ?? '' };
  }
  return {
    kind: 'span',
    ordinal: index + 1,
    before: sliceCp(text, Math.max(0, where.startCp - 40), where.startCp),
    quote: sliceCp(text, where.startCp, where.endCp),
    after: sliceCp(text, where.endCp, where.endCp + 40),
    // `position` means the stored offsets still hold the stored quote; `quote` means they did not
    // and the text was found elsewhere. Only the second is worth telling the author about.
    moved: where.state === 'quote',
  };
}

function FindingCard({
  finding,
  blocks,
  onResolve,
  resolving,
  disabled,
}: {
  finding: ReviewFinding;
  blocks: readonly Block[];
  onResolve: (finding: ReviewFinding) => void;
  resolving: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  const [copied, setCopied] = useState(false);
  const placement = placementOf(finding, blocks);
  const isOpen = finding.disposition === 'open';
  const closed =
    finding.disposition === 'open'
      ? undefined
      : t(`editor.reviewNotes.closed.${finding.disposition}`);
  // A closed note says so in its name as well as on its face: "Note 5, resolved: …".
  const name =
    finding.disposition === 'open'
      ? t('editor.reviewNotes.noteName', { ordinal: finding.ordinal, name: nameOf(finding) })
      : t('editor.reviewNotes.closedNoteName', {
          ordinal: finding.ordinal,
          state: t(`editor.reviewNotes.closedInName.${finding.disposition}`),
          name: nameOf(finding),
        });

  return (
    <Paper
      component="article"
      withBorder
      p="md"
      radius="sm"
      aria-label={name}
      style={closed === undefined ? undefined : CLOSED_CARD}
    >
      <Stack gap="xs">
        <Group gap="xs" wrap="wrap">
          <Badge
            size="sm"
            radius="sm"
            styles={{
              root: tintedBadge(SEVERITY_TOKEN[finding.severity], SEVERITY_INK[finding.severity]),
            }}
          >
            {t(`editor.reviewNotes.severity.${finding.severity}`)}
          </Badge>
          {/* BLOCKING IS INDEPENDENT OF SEVERITY — a low finding may block and a high one may not
              — so it is its own badge and never a colour on the severity one. */}
          {finding.isBlocking ? (
            <Badge
              size="sm"
              radius="sm"
              styles={{ root: tintedBadge('--mx-danger', '--mx-danger') }}
            >
              {/* The past tense once the note is closed: resolved or set aside, it holds
                  nothing up now - but it did, and the record still says so. */}
              {closed === undefined
                ? t('editor.reviewNotes.holdsUp')
                : t('editor.reviewNotes.heldUp')}
            </Badge>
          ) : (
            <Badge variant="default" size="sm" radius="sm">
              {t('editor.reviewNotes.recommended')}
            </Badge>
          )}
          {closed === undefined ? null : (
            <Badge variant="default" size="sm" radius="sm">
              {closed}
            </Badge>
          )}
        </Group>

        <FindingWords finding={finding} />

        {placement.kind === 'span' ? (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {placement.moved
                ? t('editor.reviewNotes.blockMoved', { ordinal: placement.ordinal })
                : t('editor.reviewNotes.block', { ordinal: placement.ordinal })}
            </Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              <Text span size="sm" c="dimmed">
                …{placement.before}
              </Text>
              <Text
                span
                size="sm"
                fw={700}
                style={{ backgroundColor: 'oklch(var(--mx-warning) / 0.22)' }}
              >
                {placement.quote}
              </Text>
              <Text span size="sm" c="dimmed">
                {placement.after}…
              </Text>
            </Text>
          </Stack>
        ) : null}

        {placement.kind === 'block' ? (
          <Text size="xs" c="dimmed">
            {t('editor.reviewNotes.aboutBlock', { ordinal: placement.ordinal })}
          </Text>
        ) : null}

        {placement.kind === 'article' ? (
          <Text size="xs" c="dimmed">
            {t('editor.reviewNotes.aboutArticle', { context })}
          </Text>
        ) : null}

        {placement.kind === 'lost' ? (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('editor.reviewNotes.lost', { context })}
            </Text>
            <Text size="sm" fs="italic">
              {t('editor.reviewNotes.quoted', { quote: placement.quote })}
            </Text>
          </Stack>
        ) : null}

        {/* HOW TO FIX IT, in the reason's own words (design 28 A6-P1): the body of the note now,
            at the card's own size rather than a dimmed aside. */}
        {finding.authorGuidance === undefined || finding.authorGuidance === null ? null : (
          <Text size="sm">{finding.authorGuidance}</Text>
        )}

        {finding.suggestedRewrite === undefined || finding.suggestedRewrite === null ? null : (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {t('editor.reviewNotes.suggested')}
            </Text>
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Text size="sm" fs="italic" style={{ flex: 1 }}>
                {finding.suggestedRewrite}
              </Text>
              <Button
                variant="default"
                size="compact-xs"
                onClick={() => {
                  /* A COPY BUTTON AND NEVER AUTO-APPLIED — "Nothing in the platform writes it into
                     the article" (authors-api.yaml's ReviewFinding.suggestedRewrite). */
                  void navigator.clipboard
                    ?.writeText(finding.suggestedRewrite ?? '')
                    .then(() => {
                      setCopied(true);
                    })
                    .catch(() => {
                      // A refused clipboard permission is not an error worth a banner; the text is
                      // on screen and selectable either way.
                      setCopied(false);
                    });
                }}
              >
                {copied ? t('common.copied') : t('editor.reviewNotes.copy')}
              </Button>
            </Group>
          </Stack>
        )}

        <Group justify="space-between" wrap="wrap">
          {/* NO CODE HERE (design 15, the owner's plain-words rule): `taxonomyCode` and `stage`
              ("claim.unsupported · editorial") were printed on this line, and an author reads
              neither. The span keeps the button at the right, and the lines below where they were. */}
          <span />
          {isOpen ? (
            <Button
              size="compact-sm"
              variant={finding.isBlocking ? 'filled' : 'default'}
              loading={resolving}
              disabled={disabled}
              onClick={() => {
                onResolve(finding);
              }}
            >
              {t('editor.reviewNotes.markResolved')}
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Paper>
  );
}

export function ReviewNotes({
  review,
  blocks,
  onResolve,
  resolvingOrdinal,
  disabled,
}: ReviewNotesProps) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  if (review.findings.length === 0) {
    return (
      <Stack component="section" gap="xs" aria-labelledby="editor-notes-title">
        <Title id="editor-notes-title" order={2} size="h5">
          {t('common.editorNotes')}
        </Title>
        <Text size="sm" c="dimmed">
          {/* An article with no review yet gets an EMPTY RECORD rather than a 404, so this is an
              empty state and never an error state. */}
          {t('editor.reviewNotes.none', { context })}
        </Text>
      </Stack>
    );
  }

  const open = review.openBlockingCount ?? 0;

  return (
    <Stack component="section" gap="sm" aria-labelledby="editor-notes-title">
      <Group justify="space-between" wrap="wrap">
        <Title id="editor-notes-title" order={2} size="h5">
          {t('common.editorNotes')}
        </Title>
        <Badge variant="default" size="sm" radius="sm">
          {open === 0
            ? t('editor.reviewNotes.nothingHolding')
            : t('editor.reviewNotes.stillHolding', { count: open })}
        </Badge>
      </Group>

      {review.letter === undefined || review.letter === null ? null : (
        <Paper withBorder p="md" radius="sm">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              {t('editor.reviewNotes.letter')}
            </Text>
            {/* A RENDERING OVER THE FINDINGS AND NEVER THE RECORD — the findings below are the
                record (authors-api.yaml's ReviewRecord.letter). */}
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {review.letter}
            </Text>
          </Stack>
        </Paper>
      )}

      {review.findings.map((finding) => (
        <FindingCard
          key={`${String(finding.reviewSeq)}:${String(finding.ordinal)}`}
          finding={finding}
          blocks={blocks}
          onResolve={onResolve}
          resolving={resolvingOrdinal === finding.ordinal}
          disabled={disabled}
        />
      ))}

      <Text size="xs" c="dimmed">
        {t('editor.reviewNotes.noReply')}
      </Text>
    </Stack>
  );
}

/**
 * DESIGN 28 A6-P1 (the owner, 12 October 2026: "review notes must read like a human editor wrote
 * them"). A card is named by the reason's `label` - the words of
 * docs/design/27-launch-texts/19-moderation-copy.md, served from the taxonomy version the finding was
 * judged under - and says how to fix it with `authorGuidance`. `note` arrives only on a finding a
 * person on the team wrote, and is shown under the label as that person's words: the automatic
 * review's note is the checker's staff-only English, and the server sends `null` for it. Declared
 * down here so that no cited line above moved.
 */
function nameOf(finding: ReviewFinding): string {
  return finding.label ?? finding.note ?? '';
}

function FindingWords({ finding }: { finding: ReviewFinding }) {
  return (
    <>
      {finding.label === undefined || finding.label === null ? null : (
        <Text size="sm" fw={600}>
          {finding.label}
        </Text>
      )}
      {finding.note === null ? null : <Text size="sm">{finding.note}</Text>}
    </>
  );
}
